import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { chatCreateSession } from "@/api";
import { type ChatHost, chatHost } from "./chatHost";
import { CREATED_CHAT } from "./useChatSwitchKey";

/**
 * Returns the open chat's uuid, creating the chat first for a new one (used
 * when something needs a session before the first message, e.g. uploads).
 * Concurrent callers share one create.
 */
export function useEnsureSession(
  sessionUuid: string | undefined,
  draft: {
    agentUuid: string | null;
    modelId: string | null;
    reasoningEffort: string | null;
    spaceUuid?: string | null;
    brandUuid?: string | null;
  },
  host: ChatHost = chatHost,
) {
  const navigate = useNavigate();
  const creating = useRef<Promise<string> | null>(null);
  // Uploads queued before the page re-rendered with the created chat still call the function they were given,
  // so it keeps answering with that chat until the page opens another new chat (MHS-917: two quick pastes).
  useEffect(() => {
    if (!sessionUuid) creating.current = null;
  }, [sessionUuid]);
  return (): Promise<string> => {
    if (sessionUuid) return Promise.resolve(sessionUuid);
    if (creating.current) return creating.current;
    const attempt = (async () => {
      const modelId = draft.modelId;
      if (!modelId) throw new Error("No model available");
      const { data } = await chatCreateSession({
        body: {
          modelId,
          agentUuid: draft.agentUuid,
          reasoningEffort: draft.reasoningEffort,
          ...(draft.spaceUuid ? { spaceUuid: draft.spaceUuid } : {}),
          ...(draft.brandUuid ? { brandUuid: draft.brandUuid } : {}),
          ...(host.source ? { source: host.source } : {}),
        },
        throwOnError: true,
      });
      navigate(host.pathFor(data.data.uuid), { state: CREATED_CHAT });
      return data.data.uuid;
    })();
    creating.current = attempt;
    attempt.catch(() => {
      if (creating.current === attempt) creating.current = null;
    });
    return attempt;
  };
}
